import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { FatoInput, STATUS_FATO } from "@vetbolso/contracts";
import { useState } from "react";
import type { z } from "zod";
import { api, ler, queryClient } from "../api.ts";
import type { BuscaFato } from "../buscas.ts";
import { useEu } from "../router.tsx";
import { Area, Aviso, Botao, Cabecalho, Campo, Carregando, Cartao, Entrada, Selecao, Selo } from "../ui.tsx";

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const TEXTOS = [
  ["afirmacao", "Afirmação *", true],
  ["valor", "Valor", false],
  ["unidade", "Unidade", false],
  ["localizador", "Localizador", false],
  ["citacaoCurta", "Citação curta", true],
  ["observacao", "Observação", true],
] as const;

export function PaginaFatoEditar() {
  const { id } = useParams({ from: "/area/fatos/$id" });
  const { fonte } = useSearch({ strict: false }) as z.infer<typeof BuscaFato>;
  const novo = id === "novo";
  const eu = useEu();
  const navegar = useNavigate();
  const [erros, setErros] = useState<Record<string, string>>({});
  const q = useQuery({ queryKey: ["fato", id], queryFn: () => ler(api.fatos[":id"].$get({ param: { id } })), enabled: !novo });
  const atualizar = (fonteId: string | null) => {
    queryClient.invalidateQueries({ queryKey: ["fato", id] });
    queryClient.invalidateQueries({ queryKey: ["fatos"] });
    queryClient.invalidateQueries({ queryKey: ["pendencias"] });
    if (fonteId) queryClient.invalidateQueries({ queryKey: ["fonte", fonteId] });
  };
  const salvar = useMutation({
    mutationFn: async (f: FatoInput) => {
      if (novo) return { criado: await ler(api.fatos.$post({ json: f })) };
      const { id: _id, ...resto } = f;
      return { editado: await ler(api.fatos[":id"].$put({ param: { id }, json: resto })) };
    },
    onSuccess: (r, f) => {
      atualizar(f.fonteId);
      if (r.criado) navegar({ to: "/fatos/$id", params: { id: r.criado.id } });
    },
  });

  if (!novo && !q.data) return q.error ? <Aviso>{msg(q.error)}</Aviso> : <Carregando />;
  const fato = q.data?.fato;
  const usos = q.data?.usos ?? [];
  const editor = eu?.papel === "admin" || eu?.papel === "editor";
  const fonteId = fato?.fonteId ?? fonte ?? null;

  return (
    <>
      <Cabecalho
        titulo={novo ? "Novo fato" : <span className="font-mono">{fato!.id}</span>}
        sub={
          fonteId ? (
            <Link to="/fontes/$id" params={{ id: fonteId }} className="hover:text-primary">
              Fonte {fonteId}
            </Link>
          ) : (
            "Fato derivado, sem fonte própria"
          )
        }
      />
      {salvar.data?.editado ? (
        <Aviso tom={salvar.data.editado.revalidadas ? "alerta" : "ok"}>
          Fato salvo. {salvar.data.editado.revalidadas} ficha(s) que o usam foram revalidadas.
        </Aviso>
      ) : null}
      {salvar.error ? <Aviso>{msg(salvar.error)}</Aviso> : null}
      <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
        <Cartao>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              const s = (k: string) => {
                const v = String(fd.get(k) ?? "").trim();
                return v === "" ? null : v;
              };
              let extra: unknown = {};
              try {
                extra = JSON.parse(String(fd.get("extra") || "{}"));
              } catch {
                return setErros({ extra: "JSON inválido" });
              }
              const r = FatoInput.safeParse({
                id: novo ? s("id") : fato!.id,
                fonteId: s("fonteId"),
                status: fd.get("status"),
                extra,
                ...Object.fromEntries(TEXTOS.map(([k]) => [k, s(k)])),
              });
              if (!r.success) return setErros(Object.fromEntries(r.error.issues.map((i) => [String(i.path[0]), i.message])));
              setErros({});
              salvar.mutate(r.data);
            }}
          >
            <fieldset disabled={!editor} className="grid gap-3 sm:grid-cols-2">
              {novo ? (
                <Campo rotulo="Id *" dica="ex.: E.M12" erro={erros.id}>
                  <Entrada name="id" className="font-mono" required />
                </Campo>
              ) : null}
              <Campo rotulo="Fonte" erro={erros.fonteId}>
                <Entrada name="fonteId" className="font-mono" defaultValue={fonteId ?? ""} />
              </Campo>
              <Campo rotulo="Status *" dica="Fato não verificado obriga a ref a sair marcada como pendente." erro={erros.status}>
                <Selecao name="status" defaultValue={fato?.status ?? "verificado"}>
                  {STATUS_FATO.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </Selecao>
              </Campo>
              {TEXTOS.map(([k, rotulo, largo]) => (
                <Campo key={k} rotulo={rotulo} erro={erros[k]} className={largo ? "sm:col-span-2" : undefined}>
                  {largo ? (
                    <Area name={k} rows={k === "afirmacao" ? 3 : 2} className="font-sans text-sm" defaultValue={fato?.[k] ?? ""} />
                  ) : (
                    <Entrada name={k} defaultValue={fato?.[k] ?? ""} />
                  )}
                </Campo>
              ))}
              <Campo
                rotulo="Campos do tema (JSON)"
                dica="dose_min, limiar_min… vindos da pesquisa"
                erro={erros.extra}
                className="sm:col-span-2"
              >
                <Area name="extra" rows={4} defaultValue={JSON.stringify(fato?.extra ?? {}, null, 2)} />
              </Campo>
            </fieldset>
            {editor ? (
              <Botao type="submit" variante="primario" className="self-start" disabled={salvar.isPending}>
                {salvar.isPending ? "Salvando…" : novo ? "Criar fato" : "Salvar fato"}
              </Botao>
            ) : null}
          </form>
        </Cartao>
        {!novo ? (
          <Cartao titulo={`Citado em ${usos.length} ref(s)`}>
            <ul className="flex flex-col gap-1.5 text-xs">
              {usos.map((u) => (
                <li key={`${u.fichaId}${u.caminho}`}>
                  <Link to="/fichas/$id" params={{ id: u.fichaId }} className="font-mono font-semibold text-primary">
                    {u.fichaId}
                  </Link>{" "}
                  {u.pendente ? <Selo tom="alerta">pendente</Selo> : null}
                  <div className="font-mono text-muted">{u.caminho}</div>
                </li>
              ))}
            </ul>
          </Cartao>
        ) : null}
      </div>
    </>
  );
}
